import { useMemo } from 'react';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

export const ArbitrageCalculator = () => {
  const priceData = useMemo(
    () =>
      Array.from({ length: 96 }, (_, i) => ({
        time: `${Math.floor(i / 4)
          .toString()
          .padStart(2, '0')}:${((i % 4) * 15).toString().padStart(2, '0')}`,
        buy: 110 + Math.sin(i / 10) * 30,
        sell: 108 + Math.sin(i / 10) * 30,
        hour: Math.floor(i / 4),
        interval: i,
      })),
    []
  );
  const avgPrice = useMemo(
    () => priceData.reduce((sum, p) => sum + p.buy, 0) / priceData.length,
    [priceData]
  );
  const buyThreshold = avgPrice - 10;
  const sellThreshold = avgPrice + 10;
  const opportunities = useMemo(
    () =>
      priceData.map((p) => ({
        ...p,
        opportunity: p.buy < buyThreshold ? 'buy' : p.sell > sellThreshold ? 'sell' : 'none',
      })),
    [priceData, buyThreshold, sellThreshold]
  );
  const buyOpps = opportunities
    .filter((o) => o.opportunity === 'buy')
    .sort((a, b) => a.buy - b.buy)
    .slice(0, 3);
  const sellOpps = opportunities
    .filter((o) => o.opportunity === 'sell')
    .sort((a, b) => b.sell - a.sell)
    .slice(0, 3);
  const data =
    buyOpps.length && sellOpps.length
      ? {
          buyTime: buyOpps[0].time,
          buyPrice: buyOpps[0].buy,
          sellTime: sellOpps[0].time,
          sellPrice: sellOpps[0].sell,
          profit: (sellOpps[0].sell - buyOpps[0].buy) * 0.5,
          margin: ((sellOpps[0].sell - buyOpps[0].buy) / buyOpps[0].buy) * 100,
        }
      : {
          buyTime: '00:00',
          buyPrice: 0,
          sellTime: '00:00',
          sellPrice: 0,
          profit: 0,
          margin: 0,
        };
  return (
    <DashboardCard>
      <div className="mb-4 text-base font-semibold">Best Arbitrage Opportunity</div>
      <div className="flex flex-col gap-3 text-base">
        <div className="flex items-center gap-2 rounded-md bg-blue-100 p-2.5">
          <ArrowDownCircle size={18} color="#3b82f6" />
          <div>
            <div className="font-semibold text-blue-900">BUY</div>
            <div className="text-blue-900">
              {data.buyTime} @ €{data.buyPrice.toFixed(2)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-md bg-green-100 p-2.5">
          <ArrowUpCircle size={18} color="#22c55e" />
          <div>
            <div className="font-semibold text-green-800">SELL</div>
            <div className="text-green-800">
              {data.sellTime} @ €{data.sellPrice.toFixed(2)}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-zinc-200 pt-3">
          <span className="text-zinc-500">Expected Profit</span>
          <span className="text-lg font-semibold text-green-500">
            €{data.profit.toFixed(2)}
          </span>
        </div>
        <div className="text-base text-zinc-500">
          Margin: {data.margin.toFixed(1)}% | 500 kWh capacity
        </div>
      </div>
    </DashboardCard>
  );
};

export default ArbitrageCalculator;
